/**
 * 通知 DLQ 列表（AC-H-6 / AC-H-8）。
 *
 * 對應 BE GET /admin/notifications/dlq 與 POST /admin/notifications/dlq/:id/resolve。
 *
 * Notes：
 *   - 30s 自動重抓（DLQ 數量為運維儀表板核心指標）
 *   - severity → Pill tone：P1=danger / P2,P3=warning / INFO=neutral
 *   - BE 目前回 flat shape；dispatch 欄位（kind / severity）由 `kind` 字串 + 預留
 *     `dispatch?` join 兩種來源組合，UI 做 defensive fallback。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../components/Button';
import { Pill, type PillTone } from '../components/Pill';
import {
  listNotificationDlq,
  resolveNotificationDlq,
  type NotificationChannelName,
  type NotificationDlqEntry,
  type NotificationSeverity,
} from '../lib/api-client';

const CHANNEL_LABEL: Record<NotificationChannelName, string> = {
  webhook: 'Webhook',
  sms: 'SMS',
  email: 'Email',
  line: 'LINE',
};

const SEVERITY_TONE: Record<NotificationSeverity, PillTone> = {
  P1: 'danger',
  P2: 'warning',
  P3: 'warning',
  INFO: 'neutral',
};

function severityOf(entry: NotificationDlqEntry): NotificationSeverity | null {
  if (entry.dispatch?.severity) return entry.dispatch.severity;
  return null;
}

function kindOf(entry: NotificationDlqEntry): string {
  return entry.dispatch?.kind ?? entry.kind ?? '—';
}

function truncate(text: string | null, max: number): string {
  if (!text) return '—';
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('zh-TW');
  } catch {
    return iso;
  }
}

function SeverityBadge({ severity }: { severity: NotificationSeverity | null }): JSX.Element {
  if (!severity) {
    return <Pill dot={false}>—</Pill>;
  }
  return (
    <Pill tone={SEVERITY_TONE[severity]} dot={false}>
      {severity}
    </Pill>
  );
}

export function NotificationsDlqListPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const dlqQuery = useQuery({
    queryKey: ['notifications.dlq.list'],
    queryFn: listNotificationDlq,
    refetchInterval: 30_000,
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) => resolveNotificationDlq(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications.dlq.list'] });
    },
  });

  const items: NotificationDlqEntry[] = dlqQuery.data?.items ?? [];

  /** 各 channel 計數（用於上方統計列） */
  const channelCounts = useMemo(() => {
    const counts: Record<NotificationChannelName, number> = {
      webhook: 0,
      sms: 0,
      email: 0,
      line: 0,
    };
    for (const it of items) {
      counts[it.channel] = (counts[it.channel] ?? 0) + 1;
    }
    return counts;
  }, [items]);

  const handleResolve = (entry: NotificationDlqEntry): void => {
    const kind = kindOf(entry);
    const channel = CHANNEL_LABEL[entry.channel];
    if (
      window.confirm(
        `確定要人工 resolve 此 DLQ row？\n通道：${channel}\n類型：${kind}\n重試次數：${entry.retry_count}\n\n操作會寫入 audit log，且不可復原。`,
      )
    ) {
      resolveMutation.mutate(entry.id);
    }
  };

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">通知 DLQ（H-6 / H-8）</h1>
        <div className="page-head__actions">
          <Button
            variant="secondary"
            onClick={() => dlqQuery.refetch()}
            disabled={dlqQuery.isFetching}
          >
            {dlqQuery.isFetching ? '更新中…' : '重新整理'}
          </Button>
        </div>
      </div>

      <p className="page-intro">
        通知 P1 發送失敗後進入 DLQ（AC-H-6）；本頁列出未 resolved 之 DLQ rows 並可人工處置。每 30
        秒自動更新。
      </p>

      {/* 統計列 */}
      <section
        className="card"
        style={{ display: 'flex', gap: 28, margin: '0 0 18px', padding: '14px 18px' }}
      >
        <div>
          <div style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>總筆數</div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: items.length > 0 ? 'var(--c-danger)' : 'var(--c-success)',
            }}
          >
            {items.length}
          </div>
        </div>
        {(Object.keys(CHANNEL_LABEL) as NotificationChannelName[]).map((ch) => (
          <div key={ch}>
            <div style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>{CHANNEL_LABEL[ch]}</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{channelCounts[ch]}</div>
          </div>
        ))}
      </section>

      {dlqQuery.isLoading && <div>載入中…</div>}
      {dlqQuery.error && <div className="error-banner">{(dlqQuery.error as Error).message}</div>}
      {resolveMutation.error && (
        <div className="error-banner">
          人工 resolve 失敗：{(resolveMutation.error as Error).message}
        </div>
      )}

      {dlqQuery.data && (
        <div className="table-card">
          <table>
            <thead>
              <tr>
                <th>通道</th>
                <th>類型 kind</th>
                <th>等級</th>
                <th style={{ textAlign: 'right' }}>重試</th>
                <th>下次重試</th>
                <th>最後錯誤</th>
                <th style={{ width: 200 }}>動作</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty-cell">
                    目前無未處理之 DLQ 項目
                  </td>
                </tr>
              )}
              {items.map((entry) => {
                const errorText = entry.last_error;
                const errorTrim = truncate(errorText, 60);
                return (
                  <tr key={entry.id}>
                    <td>
                      <Pill dot={false}>{CHANNEL_LABEL[entry.channel]}</Pill>
                    </td>
                    <td style={{ fontSize: 13 }}>{kindOf(entry)}</td>
                    <td>
                      <SeverityBadge severity={severityOf(entry)} />
                    </td>
                    <td style={{ textAlign: 'right' }}>{entry.retry_count}</td>
                    <td style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
                      {formatDateTime(entry.next_retry_at)}
                    </td>
                    <td
                      style={{ fontSize: 12, color: 'var(--c-text-muted)', maxWidth: 320 }}
                      title={errorText ?? ''}
                    >
                      {errorTrim}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button
                          variant="secondary"
                          onClick={() => navigate(`/notifications/${entry.dispatch_id}`)}
                        >
                          查 dispatch
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => handleResolve(entry)}
                          disabled={resolveMutation.isPending}
                        >
                          人工 resolve
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
